/** External */
import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";

/** Internal */
import { useI18n } from "Hooks";
import { compose, graphql } from "lib/apolloClient";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import UserNameAvatar from "AppComponents/UserNameAvatar";
import { Button, Dialog } from "@toddle-edu/ds-web";
import { getUserIdentifierName } from "Utils/GetFormattedUserName";
import { getFormattedStudentName } from "Utils/GetFormattedStudentName";
import { FAMILY_INVITE_STATUS_INVITED } from "Constants/stringConstants";

/* Relative **/
import {
  getChildrenQueryVariables,
  isExpiredToken,
  getChildrenRefetchQueries,
} from "Platform/routes/FamilyProfile/modules/Utils";
import classes from "./PendingInvitationBannerV2.scss";
import styles from "./PendingInvitationBannerV2Styles";
import { addChild } from "Courses/modules/CoursesModule";
import { getChildrenQuery } from "modules/CommonQuery";
import { goToRelativeRoute } from "modules/Services";
import { getChildrenFromCache } from "modules/CommonGraphqlHelpers";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";
import {
  setToastMsg,
  getUserInfo,
  updateChildId,
  switchChild,
  handleAcademicYearForParent,
} from "Login/modules/LoginModule";
import { declineFamilyInvitation } from "FamilyProfile/modules/FamilyProfileModule";
import { getPlatformUserSessionDetailsFromCache } from "Platform/modules/PlatformGraphqlHelpers";

const ConfirmEmailInviteDecline = ({
  onConfirmDeclineInvitation,
  onCloseClick,
  student,
  orderBy,
  t,
}) => {
  const studentFullName = getFormattedStudentName({
    student,
    orderBy,
  });

  const Body = () => {
    return (
      <div>
        {t("common:decline_invitation_subtext_v2", {
          label: studentFullName,
        })}
      </div>
    );
  };

  return (
    <Dialog
      dsVersion="2.0"
      variant="delete-dialog"
      title={t("common:reject_invitation")}
      onClose={onCloseClick}
      message={<Body />}
      buttonOneText={t("common:cancel")}
      buttonTwoText={t("common:reject_invitation")}
      onButtonOneClick={onCloseClick}
      onButtonTwoClick={onConfirmDeclineInvitation}
    ></Dialog>
  );
};

const PendingInvitationBannerV2 = props => {
  const {
    userId,
    userType,
    userEntityType,
    pendingInvitations,
    addChild,
    setToastMsg,
    childrenQueryVariables,
    declineFamilyInvitation,
    orderBy,
    updateChildId,
    switchChild,
    shouldUpdateChildId = false,
    goToRelativeRoute,
    handleAcademicYearForParent,
  } = props;

  const { t } = useI18n();

  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({});
  const [confirmEmailInviteDeclineModal, setEmailInviteDeclineModal] = useState(
    false
  );

  const pendingInvitationsCount = _.size(pendingInvitations);

  if (pendingInvitationsCount === 0) {
    return null;
  }

  const childrenRefetchQueries = getChildrenRefetchQueries({
    variables: childrenQueryVariables,
    userId,
    userType,
  });

  const showEmailInviteDeclineModal = ({ student }) => {
    setSelectedStudent(student);
    setEmailInviteDeclineModal(true);
  };

  const hideEmailInviteDeclineModal = () => {
    setSelectedStudent({});
    setEmailInviteDeclineModal(false);
  };

  const addChildHandler = async ({ studentCode, userId }) => {
    const data = getPlatformUserSessionDetailsFromCache({
      id: userId,
      type: userEntityType,
    });

    const isSessionVerified = _.get(data, "isSessionVerified", true);

    setFullScreenLoader(true);

    const childData = await addChild({
      studentCode,
      userId,
      signInFlow: false,
      refetchQueries: childrenRefetchQueries,
      includePlatformUserQuery: true,
    });

    if (!_.isEmpty(childData)) {
      setToastMsg({ msg: "toastMsgs:invitation_accepted", type: "success" });
    }

    if (shouldUpdateChildId) {
      updateChildId(_.get(childData, "id", ""));
    }

    if (isSessionVerified) {
      switchChild({ childData });

      handleAcademicYearForParent();
      // do routing
      goToRelativeRoute({
        route: "/",
      });
    }

    setFullScreenLoader(false);
  };

  const handleAddChild = ({ student }) => {
    addChildHandler({
      studentCode: student.familyInviteCode,
      userId,
    });
  };

  const handleDeclineInvitation = async ({ parentId, childId }) => {
    setFullScreenLoader(true);

    await declineFamilyInvitation({
      parentId,
      childId,
    });

    setFullScreenLoader(false);
    hideEmailInviteDeclineModal();
  };

  const onConfirmDeclineInvitation = () => {
    handleDeclineInvitation({
      parentId: userId,
      childId: _.get(selectedStudent, "id"),
    });
  };

  const firstStudent = pendingInvitations[0];
  const childIdentifier = getUserIdentifierName({ user: firstStudent });

  return (
    <div className={classes.bannerContainer}>
      <div className="text-label-l">
        {_.size(pendingInvitations) === 1 ? (
          <>
            {t("common:pending_invite_label_with_name", {
              name: childIdentifier,
            })}
          </>
        ) : (
          <> {t("common:pending_invite_label_for_multiple_accounts")}</>
        )}
      </div>
      <div className={classes.pendingInvitationsContainer}>
        {pendingInvitations && _.size(pendingInvitations)
          ? _.map(pendingInvitations, (invitation, index) => {
              const student = invitation;
              const { profileImage } = student;
              const childIdentifier = getUserIdentifierName({ user: student });

              const course = _.get(student, "allCourses[0]", {});
              const curriculumProgramType = _.get(
                course,
                "curriculumProgram.acronym",
                ""
              );

              return (
                <div
                  className={classes.pendingInvitationContent}
                  key={student.id}
                >
                  <div className={classes.bannerContent}>
                    <UserNameAvatar
                      dsVersion="2.0"
                      id={student.id}
                      profileImage={profileImage}
                      name={childIdentifier}
                      viewMode={"NAME_AND_CUSTOM_SUBTEXT"}
                      shouldDynamicColor={true}
                      showTooltip={false}
                      showAvatar={true}
                      customNameClass={"text-body"}
                      avatarStyles={styles.avatarStyle}
                      linkStyles={styles.linkStyle}
                      customSubText={curriculumProgramType}
                      customSubTextStyle={styles.subTextStyle}
                    />
                    <div className={classes.buttonsContainer}>
                      <Button
                        dsVersion="2.0"
                        variant="neutral"
                        type="outlined"
                        size="medium"
                        onClick={() => showEmailInviteDeclineModal({ student })}
                      >
                        {t("common:reject")}
                      </Button>
                      <Button
                        dsVersion="2.0"
                        variant="progressive"
                        size="medium"
                        onClick={() => handleAddChild({ student })}
                      >
                        {t("common:accept")}
                      </Button>
                    </div>
                  </div>
                  {index !== _.size(pendingInvitations) - 1 && (
                    <div className={classes.divider}></div>
                  )}
                </div>
              );
            })
          : null}
      </div>
      {confirmEmailInviteDeclineModal ? (
        <ConfirmEmailInviteDecline
          onConfirmDeclineInvitation={onConfirmDeclineInvitation}
          onCloseClick={hideEmailInviteDeclineModal}
          student={selectedStudent}
          orderBy={orderBy}
          t={t}
        />
      ) : null}
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo(state);
  const userId = _.get(userInfo, "id");
  const userType = _.get(userInfo, "user_type");
  const userEntityType = _.get(userInfo, "userEntityType");
  const organizationId = _.get(userInfo, "org_id");

  const childrenQueryVariables = getChildrenQueryVariables({
    familyMemberId: userId,
    user_type: userType,
    organizationId,
    userId,
    includeInvitedStudents: true,
  });

  return {
    userId,
    childrenQueryVariables,
    userType,
    userEntityType,
    orderBy: getDefaultNameSortPreference(state),
    isData: true,
    isLoading: false,
  };
};

const mapActionCreators = {
  addChild,
  setToastMsg,
  declineFamilyInvitation,
  updateChildId,
  switchChild,
  goToRelativeRoute,
  handleAcademicYearForParent,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getChildrenQuery, {
    name: "getChildren",
    options: ({ childrenQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: childrenQueryVariables,
    }),
    props({
      getChildren,
      ownProps: { childrenQueryVariables, isData, isLoading },
    }) {
      const childrenData = getChildrenFromCache(childrenQueryVariables);
      const childrenInfo = _.get(childrenData, "children", []);

      const pendingInvitations = _.filter(
        childrenInfo,
        childInfo =>
          _.get(childInfo, "parentStatus") === FAMILY_INVITE_STATUS_INVITED &&
          !isExpiredToken(_.get(childInfo, "inviteExpireTime"))[0]
      );

      return {
        isData: !_.isEmpty(childrenInfo) && isData,
        isLoading:
          _.includes([1, 2], getChildren["networkStatus"]) || isLoading,
        pendingInvitations,
      };
    },
  }),
  withLoader
)(PendingInvitationBannerV2);
